import { emitCocoEvent, COCO_EVENTS } from '@theme/theme-events';
import { HTMLUpdateUtility } from '@theme/coco-utilities';

const VARIANT_CHANGE = COCO_EVENTS.variantChanged;
if (!customElements.get('coco-variants')) {
  class CocoVariants extends HTMLElement {
    constructor() {
      super();
      this.currentVariant = null;
      this.currentOptions = [];
      this.abortController = undefined;
      this.onPickerClick = this.onPickerClick.bind(this);
      this.onSelectChange = this.onSelectChange.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
    }

    getSectionRoot() {
      return (
        this.closest('product-info') ||
        this.closest('[is="quick-view"]') ||
        document.getElementById(`shopify-section-${this.sectionId}`) ||
        this.closest('.shopify-section') ||
        null
      );
    }

    isQuickView() {
      return Boolean(this.closest('[is="quick-view"], .quick-view'));
    }

    shouldUpdateBrowserUrl() {
      if (this.isQuickView()) return false;

      const root = this.closest('product-info');
      if (root?.dataset?.updateUrl === 'false') return false;
      if (root?.dataset?.updateUrl === 'true') return true;

      // Fallback: only when this product-info is the page's main product section
      // and the current path is already a product URL for this product.
      if (!root || !window.location.pathname.includes('/products/')) return false;
      return Boolean(this.closest(`product-info#shopify-section-${this.sectionId}`));
    }

    isMainProductPage() {
      return this.shouldUpdateBrowserUrl();
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl || '';
      this.sectionEl = this.getSectionRoot();

      this.form =
        this.sectionEl?.querySelector(`#${CSS.escape(this.dataset.productForm)}`) ||
        this.sectionEl?.querySelector(`coco-atc[data-product-form="${this.dataset.productForm}"] form`);

      this.variantInput = this.form?.querySelector('[name="id"]');
      this.variants = this.readJson('[data-product-variants]');
      this.productId = this.dataset.productId;

      this.addEventListener('click', this.onPickerClick);
      this.addEventListener('change', this.onSelectChange);

      this.mediaMap = this.readMediaMap();
      this.qtyInput = this.sectionEl?.querySelector('[data-product-qty]');
      this.qtyInput?.addEventListener('change', this.onQuantityChange);

      const initialVariantId =
        this.variantInput?.value ||
        (this.shouldUpdateBrowserUrl() ? this.getVariantIdFromUrl() : null);
      if (initialVariantId) {
        const variant = this.getVariantById(initialVariantId);
        if (variant) {
          this.syncPickerFromVariant(variant);
          this.currentOptions = this.getSelectedOptions();
          this.applyVariant(variant, { updateUrl: false, skipRenderProductInfo: true });
        }
      } else {
        this.currentOptions = this.getSelectedOptions();
        this.updatePickerAvailability(this.currentOptions);
      }
    }

    syncPickerFromVariant(variant) {
      [variant.option1, variant.option2, variant.option3].forEach((value, index) => {
        if (!value) return;

        const btn = Array.from(this.querySelectorAll(`[data-variant-picker-value][data-option-index="${index}"]`)).find(
          (el) => el.dataset.optionValue === value,
        );
        if (btn) this.setSelectedPicker(btn);

        const select = this.querySelector(`[data-variant-picker-select][data-option-index="${index}"]`);
        if (select) select.value = value;

        const label = this.querySelector(`[data-selected-option="${index}"]`);
        if (label) label.textContent = value;
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onPickerClick);
      this.removeEventListener('change', this.onSelectChange);
      this.qtyInput?.removeEventListener('change', this.onQuantityChange);
      this.abortController?.abort();
    }

    readJson(selector) {
      const el = this.querySelector(selector);
      if (!el?.textContent) return [];
      try {
        return JSON.parse(el.textContent);
      } catch (_e) {
        return [];
      }
    }

    getVariantIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return params.get('variant');
    }

    resolvePickerFromEvent(event) {
      const picker = event.target.closest('[data-variant-picker-value]');
      if (picker && this.contains(picker)) return picker;

      const input = event.target.closest('[data-variant-picker-input]');
      if (!input || !this.contains(input)) return null;

      return this.querySelector(
        `[data-variant-picker-value][data-option-index="${input.dataset.optionIndex}"][data-option-value="${CSS.escape(input.value)}"]`,
      );
    }

    /**
     * Combined listing option values point at another product URL.
     */
    isCombinedListingProductUrl(url) {
      if (!url) return false;
      try {
        const next = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
        const current = new URL(this.productUrl, window.location.origin).pathname.replace(/\/$/, '');
        return next !== current;
      } catch (_e) {
        return false;
      }
    }

    getOptionProductUrl(target) {
      if (!target) return '';
      if (target.tagName === 'SELECT') {
        return target.selectedOptions?.[0]?.dataset?.productUrl || '';
      }
      return target.dataset?.productUrl || '';
    }

    onPickerClick(event) {
      const picker = this.resolvePickerFromEvent(event);
      if (!picker) return;

      // Buttons only — radio/label selections go through `change`.
      if (picker.tagName !== 'BUTTON') return;

      event.preventDefault();
      this.setSelectedPicker(picker);
      this.updateVariant(picker);
    }

    onSelectChange(event) {
      if (!this.contains(event.target)) return;

      const select = event.target.closest('[data-variant-picker-select]');
      if (select) {
        this.markSelectedOption(select);
        this.updateVariant(select);
        return;
      }

      const input = event.target.closest('[data-variant-picker-input]');
      if (!input) return;

      const picker = this.querySelector(
        `[data-variant-picker-value][data-option-index="${input.dataset.optionIndex}"][data-option-value="${CSS.escape(input.value)}"]`,
      );
      if (!picker) return;

      this.setSelectedPicker(picker);
      this.updateVariant(picker);
    }

    markSelectedOption(select) {
      if (select?.tagName !== 'SELECT' || !select.selectedOptions?.length) return;
      Array.from(select.options)
        .find((opt) => opt.hasAttribute('selected'))
        ?.removeAttribute('selected');
      select.selectedOptions[0].setAttribute('selected', '');
    }

    normalizeProductPath(url) {
      if (!url) return '';
      try {
        return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
      } catch (_e) {
        return String(url).split('?')[0].replace(/\/$/, '');
      }
    }

    /** Sleek-style option value IDs for Section Rendering `option_values`. */
    getSelectedOptionValueIds() {
      const ids = [];
      const optionCount = parseInt(this.dataset.optionCount, 10) || 0;

      for (let i = 0; i < optionCount; i += 1) {
        const selectedBtn = this.querySelector(
          `[data-variant-picker-value][data-option-index="${i}"].is-selected`,
        );
        if (selectedBtn?.dataset?.optionValueId) {
          ids.push(selectedBtn.dataset.optionValueId);
          continue;
        }

        const checkedInput = this.querySelector(
          `[data-variant-picker-input][data-option-index="${i}"]:checked`,
        );
        if (checkedInput?.dataset?.optionValueId) {
          ids.push(checkedInput.dataset.optionValueId);
          continue;
        }

        const select = this.querySelector(`[data-variant-picker-select][data-option-index="${i}"]`);
        const selectedOption = select?.selectedOptions?.[0];
        if (selectedOption?.dataset?.optionValueId) {
          ids.push(selectedOption.dataset.optionValueId);
        }
      }

      return ids.filter(Boolean);
    }

    scrollProductSurfaceToTop(anchor) {
      const node = anchor || this;
      const quickView = node.closest?.('[is="quick-view"]');
      const qvScroller = quickView?.querySelector('[data-quick-view-content]');
      if (qvScroller) {
        qvScroller.scrollTop = 0;
        return;
      }

      const root =
        (node.matches?.('product-info') && node) ||
        node.querySelector?.('product-info') ||
        node.closest?.('product-info') ||
        node;
      const target =
        root.querySelector?.('.product-info-wrapper') ||
        root.querySelector?.('.product') ||
        root;
      if (!target) return;

      requestAnimationFrame(() => {
        if (window.lenis?.scrollTo) {
          window.lenis.scrollTo(target, { offset: 0, immediate: true });
          return;
        }
        const top = target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
      });
    }

    setSelectedPicker(active) {
      const index = active.dataset.optionIndex;
      this.querySelectorAll(`[data-variant-picker-value][data-option-index="${index}"]`).forEach((el) => {
        const selected = el === active;
        el.classList.toggle('is-selected', selected);
        if (el.tagName === 'BUTTON') {
          el.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }

        const radio = this.querySelector(
          `[data-variant-picker-input][data-option-index="${index}"][value="${CSS.escape(el.dataset.optionValue || '')}"]`,
        );
        if (radio) radio.checked = selected;
      });

      const label = this.querySelector(`[data-selected-option="${index}"]`);
      if (label) label.textContent = active.dataset.optionValue || active.textContent.trim();
    }

    getSelectedOptions() {
      const options = [];
      const optionCount = parseInt(this.dataset.optionCount, 10) || 0;

      for (let i = 0; i < optionCount; i += 1) {
        const selectedBtn = this.querySelector(
          `[data-variant-picker-value][data-option-index="${i}"].is-selected`,
        );
        if (selectedBtn) {
          options[i] = selectedBtn.dataset.optionValue;
          continue;
        }

        const checkedInput = this.querySelector(
          `[data-variant-picker-input][data-option-index="${i}"]:checked`,
        );
        if (checkedInput) {
          options[i] = checkedInput.value;
          continue;
        }

        const select = this.querySelector(`[data-variant-picker-select][data-option-index="${i}"]`);
        if (select) options[i] = select.value;
      }

      return options;
    }

    getVariantFromOptions(options) {
      if (!this.variants?.length) return null;
      return (
        this.variants.find((variant) =>
          options.every((value, index) => {
            if (!value) return true;
            return variant[`option${index + 1}`] === value;
          }),
        ) || null
      );
    }

    findVariantByOptions(requireAvailability) {
      const options = this.currentOptions;
      if (!this.variants?.length) return null;

      return (
        this.variants.find((variant) => {
          const matches = options.every((value, index) => {
            if (!value) return true;
            return variant[`option${index + 1}`] === value;
          });
          return requireAvailability ? matches && variant.available : matches;
        }) || null
      );
    }

    findClosestAvailableVariant(lastOptionIndex) {
      if (lastOptionIndex == null || Number.isNaN(lastOptionIndex)) return null;
      if (!this.variants?.length) return null;

      const prefix = this.currentOptions.slice(0, lastOptionIndex);
      const matches = this.variants.filter(
        (variant) =>
          variant.available &&
          prefix.every((value, index) => !value || variant[`option${index + 1}`] === value),
      );

      return matches.reduce((best, variant) => {
        if (!best) return variant;

        const score = (candidate) =>
          this.currentOptions.reduce(
            (sum, value, index) =>
              value && candidate[`option${index + 1}`] === value ? sum + (3 - index) : sum,
            0,
          );

        return score(variant) > score(best) ? variant : best;
      }, null);
    }

    getVariantById(id) {
      return this.variants.find((v) => String(v.id) === String(id)) || null;
    }

    getLastChangedOptionIndex(target) {
      if (!target) return undefined;
      const index = parseInt(target.getAttribute('data-option-index'), 10);
      return Number.isNaN(index) ? undefined : index;
    }

    updateVariant(lastChangedTarget) {
      this.currentOptions = this.getSelectedOptions();

      const productUrl = this.getOptionProductUrl(lastChangedTarget);
      if (productUrl && this.isCombinedListingProductUrl(productUrl)) {
        this.renderProductInfo(null, this.getSectionRoot(), { productUrl, swap: true });
        return;
      }

      const lastIndex = this.getLastChangedOptionIndex(lastChangedTarget);

      // Prefer the exact match (incl. sold-out) so unavailable options stay selected.
      // Only jump to closest available when the combination does not exist.
      this.currentVariant =
        this.findVariantByOptions(true) ||
        this.findVariantByOptions(false) ||
        this.findClosestAvailableVariant(lastIndex) ||
        null;

      if (this.currentVariant) {
        this.syncPickerFromVariant(this.currentVariant);
        this.currentOptions = this.getSelectedOptions();
      }

      this.applyVariant(this.currentVariant);
    }

    applyVariant(variant, { updateUrl = true, skipRenderProductInfo = false } = {}) {
      this.currentVariant = variant || null;

      if (variant && this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;
      } else if (this.variantInput) {
        this.variantInput.value = '';
        this.variantInput.disabled = true;
      }

      if (updateUrl && variant?.id && this.shouldUpdateBrowserUrl()) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      const section = this.getSectionRoot();

      this.updatePickerAvailability(this.getSelectedOptions(), variant);
      if (!skipRenderProductInfo) {
        this.renderProductInfo(variant, section);
      }
      this.updateBuyButton(variant, section);

      emitCocoEvent(VARIANT_CHANGE, {
        variant,
        sectionId: this.sectionId,
        productId: this.productId,
      });

      this.dispatchEvent(
        new CustomEvent(VARIANT_CHANGE, {
          bubbles: true,
          detail: { variant, sectionId: this.sectionId },
        }),
      );
    }

    regionId(name) {
      return `${name}-${this.dataset.section}-${this.dataset.productId}`;
    }

    patchRegionFromHtml(responseText, regionId, { transition = false } = {}) {
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const destination = this.sectionEl?.querySelector(`#${CSS.escape(regionId)}`);
      const source = parsedHTML.getElementById(regionId);
      if (!source || !destination) return;

      if (transition) {
        HTMLUpdateUtility.viewTransition(destination, source);
        return;
      }

      destination.innerHTML = source.innerHTML;
      destination.toggleAttribute('hidden', source.hasAttribute('hidden'));
    }

    patchVariantPickerFromHtml(responseText, regionId, variant) {
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const destination = this.sectionEl?.querySelector(`#${CSS.escape(regionId)}`);
      const source = parsedHTML.getElementById(regionId);
      if (!source || !destination) return false;

      const destPicker = destination.querySelector('coco-variants');
      const sourcePicker = source.querySelector('coco-variants');
      if (!destPicker || !sourcePicker) {
        destination.innerHTML = source.innerHTML;
        return false;
      }

      const focusSnapshot = this.capturePickerFocus();
      // Keep the live coco-variants host — swapping it would disconnect `this`.
      HTMLUpdateUtility.setInnerHTML(destPicker, sourcePicker.innerHTML);
      this.variants = this.readJson('[data-product-variants]');
      if (variant) this.syncPickerFromVariant(variant);
      this.restorePickerFocus(focusSnapshot);
      return true;
    }

    /**
     * Snapshot of keyboard focus inside this picker so we can restore after
     * innerHTML re-render (arrow-key radio changes otherwise jump focus to body).
     */
    capturePickerFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;

      if (active.matches?.('[data-variant-picker-input]')) {
        return {
          kind: 'input',
          optionIndex: active.dataset.optionIndex,
          value: active.value,
        };
      }

      if (active.matches?.('[data-variant-picker-select]')) {
        return {
          kind: 'select',
          optionIndex: active.dataset.optionIndex,
        };
      }

      const valueEl = active.closest?.('[data-variant-picker-value]');
      if (valueEl && this.contains(valueEl)) {
        return {
          kind: 'value',
          optionIndex: valueEl.dataset.optionIndex,
          value: valueEl.dataset.optionValue,
        };
      }

      return null;
    }

    restorePickerFocus(snapshot) {
      if (!snapshot || !this.isConnected) return;

      let target = null;

      if (snapshot.kind === 'select') {
        target = this.querySelector(
          `[data-variant-picker-select][data-option-index="${snapshot.optionIndex}"]`,
        );
      } else if (snapshot.kind === 'input') {
        const value = snapshot.value || '';
        target =
          this.querySelector(
            `[data-variant-picker-input][data-option-index="${snapshot.optionIndex}"][value="${CSS.escape(value)}"]`,
          ) ||
          this.querySelector(
            `[data-variant-picker-input][data-option-index="${snapshot.optionIndex}"]:checked`,
          );
      } else if (snapshot.kind === 'value') {
        const value = snapshot.value || '';
        target =
          this.querySelector(
            `[data-variant-picker-value][data-option-index="${snapshot.optionIndex}"][data-option-value="${CSS.escape(value)}"]`,
          ) ||
          this.querySelector(
            `[data-variant-picker-value][data-option-index="${snapshot.optionIndex}"].is-selected`,
          );
      }

      target?.focus?.({ preventScroll: true });
    }

    refreshSectionRefs(section) {
      this.sectionEl = section || this.getSectionRoot();
      this.form =
        this.sectionEl?.querySelector(`#${CSS.escape(this.dataset.productForm)}`) ||
        this.sectionEl?.querySelector(`coco-atc[data-product-form="${this.dataset.productForm}"] form`);
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.mediaMap = this.readMediaMap();

      const nextQtyInput = this.sectionEl?.querySelector('[data-product-qty]');
      if (nextQtyInput !== this.qtyInput) {
        this.qtyInput?.removeEventListener('change', this.onQuantityChange);
        this.qtyInput = nextQtyInput;
        this.qtyInput?.addEventListener('change', this.onQuantityChange);
      }
    }

    setUnavailable(section = this.getSectionRoot()) {
      this.updateBuyButton(null, section);

      const regionNames = [
        'Price',
        'Inventory',
        'Sku',
        'Badges', 
        'Volume-pricing',
        'Back-in-stock',
      ];

      regionNames.forEach((name) => {
        section?.querySelector(`#${CSS.escape(this.regionId(name))}`)?.setAttribute('hidden', '');
      });
    }

    /**
     * Hit product URL + section_id (+ option_values for combined listings).
     */
    buildSectionRequestUrl(variantId, productUrl) {
      const url = new URL(
        productUrl || this.dataset.productUrl || window.location.pathname,
        window.location.origin,
      );
      if (variantId) url.searchParams.set('variant', String(variantId));
      url.searchParams.set('section_id', this.sectionId);
      const optionValues = this.getSelectedOptionValueIds();
      if (optionValues.length) {
        url.searchParams.set('option_values', optionValues.join(','));
      }
      return url;
    }

    async fetchSectionHtml(path, sectionId, extraParams = {}) {
      const fetchUrl = new URL(path, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value != null && value !== '') fetchUrl.searchParams.set(key, String(value));
      });

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error('Failed to load section');
      return response.text();
    }

    /**
     * Sleek-style product info update:
     * - same product → patch regions
     * - combined listing product URL change → viewTransition product-info
     */
    renderProductInfo(variant, section, { productUrl, swap = false } = {}) {
      if (swap && productUrl) {
        this.swapProductInfo(productUrl);
        return;
      }

      if (!variant?.id) {
        this.setUnavailable(section);
        return;
      }

      const url = this.buildSectionRequestUrl(variant.id);

      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(url.toString(), { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          const sectionId = this.sectionId;
          const focusSnapshot = this.capturePickerFocus();

          this.patchRegionFromHtml(responseText, this.regionId('Badges'));
          this.patchRegionFromHtml(responseText, this.regionId('Price'));
          this.patchRegionFromHtml(responseText, this.regionId('Inventory'));
          this.patchRegionFromHtml(responseText, this.regionId('Product-actions'));
          this.patchRegionFromHtml(responseText, this.regionId('Volume-pricing'));
          this.patchVariantPickerFromHtml(responseText, this.regionId('Variant-picker'), variant);
          this.patchRegionFromHtml(responseText, this.regionId('Product-media-gallery'), {
            transition: true,
          });
          this.patchRegionFromHtml(responseText, this.regionId('Back-in-stock'));
          this.patchRegionFromHtml(responseText, this.regionId('Sku'));

          const updatedSection = this.getSectionRoot();

          this.refreshSectionRefs(updatedSection);
          this.updatePickerAvailability(this.getSelectedOptions(), variant);
          this.updateBuyButton(variant, updatedSection);
          this.restorePickerFocus(focusSnapshot);

          window?.Shopify?.PaymentButton?.init?.();

          document.dispatchEvent(
            new CustomEvent('productInfo:loaded', {
              detail: { variant, sectionId, productId: this.productId },
            }),
          );
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
        });
    }

    /** Combined listing / product swap — replace product-info (sleek handleSwapProduct). */
    async swapProductInfo(productUrl) {
      const path = this.normalizeProductPath(productUrl);
      if (!path) {
        if (this.shouldUpdateBrowserUrl()) window.location.href = productUrl;
        return;
      }

      const quickView = this.closest('[is="quick-view"]');
      const updateUrl = this.shouldUpdateBrowserUrl();
      const currentInfo = this.closest('product-info');
      const optionValues = this.getSelectedOptionValueIds();
      const extras = optionValues.length ? { option_values: optionValues.join(',') } : {};

      this.classList.add('is-loading');
      quickView?.classList.add('is-loading');

      const postProcess = (newNode) => {
        window?.Shopify?.PaymentButton?.init?.();
        document.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: this.sectionId },
          }),
        );
        this.scrollProductSurfaceToTop(newNode || quickView);
      };

      try {
        const responseText = await this.fetchSectionHtml(path, this.sectionId, extras);
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (quickView) {
          const fetchedSection =
            html.getElementById(`shopify-section-${this.sectionId}`) ||
            html.querySelector(`#shopify-section-${this.sectionId}`);
          const newContent =
            fetchedSection?.querySelector('[data-quick-view-content]') ||
            html.querySelector('[data-quick-view-content]');
          const currentContent = quickView.querySelector('[data-quick-view-content]');
          if (!newContent || !currentContent) throw new Error('Quick view content missing');

          const oldInfo = currentContent.querySelector('product-info');
          const nextInfo = newContent.querySelector('product-info') || newContent.firstElementChild;
          if (oldInfo && nextInfo) {
            HTMLUpdateUtility.viewTransition(oldInfo, nextInfo, [], [postProcess]);
          } else {
            HTMLUpdateUtility.setInnerHTML(currentContent, newContent.innerHTML);
            postProcess(currentContent);
          }
          return;
        }

        const sectionRoot =
          html.getElementById(`shopify-section-${this.sectionId}`) ||
          html.querySelector(`#shopify-section-${this.sectionId}`);
        const newInfo =
          sectionRoot?.querySelector('product-info') ||
          html.querySelector(`product-info[data-section-id="${this.sectionId}"]`) ||
          (sectionRoot?.matches?.('product-info') ? sectionRoot : null) ||
          html.querySelector('product-info');
        const oldInfo =
          currentInfo ||
          document.querySelector(`product-info[data-section-id="${this.sectionId}"]`) ||
          document.getElementById(`shopify-section-${this.sectionId}`);

        if (!newInfo || !oldInfo) throw new Error('Product info missing');

        if (!updateUrl) {
          newInfo.setAttribute('data-update-url', 'false');
          newInfo.setAttribute('data-section-id', this.sectionId);
          if (oldInfo.id) newInfo.id = oldInfo.id;
          if (oldInfo.className) newInfo.className = oldInfo.className;
        }

        try {
          const destination = new URL(productUrl, window.location.origin);
          const next = `${destination.pathname}${destination.search}`;
          if (`${window.location.pathname}${window.location.search}` !== next) {
            window.history.replaceState({}, '', next);
          }
        } catch (_) {
          /* ignore */
        }

        HTMLUpdateUtility.viewTransition(oldInfo, newInfo, [], [postProcess]);
      } catch (error) {
        console.error(error);
        if (updateUrl && !quickView) window.location.href = productUrl;
      } finally {
        this.classList.remove('is-loading');
        quickView?.classList.remove('is-loading');
      }
    }

    updateBuyButton(variant, section) {
      const productForm =
        section?.querySelector(`coco-atc[data-product-form="${this.dataset.productForm}"]`) ||
        section?.querySelector('coco-atc');
      const submit = productForm?.querySelector('[type="submit"][name="add"]');
      const backInStock = section?.querySelector(`#Back-in-stock-${this.sectionId}-${this.productId}`);
      if (!submit) return;

      const textEl = submit.querySelector('[data-product-atc-text]');
      if (!variant) {
        submit.setAttribute('disabled', 'disabled');
        if (textEl) textEl.textContent = window.Coco.variantStrings?.unavailable || 'Unavailable';
        if (backInStock) backInStock.classList.add('hidden');
        return;
      } else if (!variant.available) {
        submit.setAttribute('disabled', 'disabled');
        if (textEl) textEl.textContent = window.Coco.variantStrings?.soldOut || 'Sold out';
        if (backInStock) backInStock.classList.remove('hidden');
        return;
      } else {
        submit.removeAttribute('disabled');
        if (textEl) textEl.textContent = window.Coco.variantStrings?.addToCart || 'Add to cart';
        if (backInStock) backInStock.classList.add('hidden');
      }
    }

    onQuantityChange() {
      this.updateVolumePricingActiveTier(this.sectionEl);
    }

    updateVolumePricingActiveTier(section) {
      const wrapper = section?.querySelector('[data-product-volume-pricing]');
      const qtyInput = section?.querySelector('[data-product-qty]');
      const tierList = wrapper?.querySelector('[data-volume-pricing-tier-list]');
      if (!tierList || !qtyInput || wrapper.hidden) return;

      const currentQty = parseInt(qtyInput.value, 10) || 1;
      const tiers = [...tierList.querySelectorAll('[data-tier-qty]')];
      if (!tiers.length) return;

      let activeTier = tiers[0];
      tiers.forEach((tier) => {
        const tierQty = parseInt(tier.dataset.tierQty, 10);
        if (currentQty >= tierQty) activeTier = tier;
      });

      tiers.forEach((tier) => {
        tier.classList.toggle('is-active', tier === activeTier);
      });
    }

    updatePickerAvailability(selectedOptions, currentVariant = this.currentVariant) {
      this.querySelectorAll('[data-variant-picker-value]').forEach((btn) => {
        if (btn.dataset.productUrl && this.isCombinedListingProductUrl(btn.dataset.productUrl)) {
          btn.classList.remove('is-unavailable', 'is-sold-out', 'disabled');
          const siblingInput = this.querySelector(
            `[data-variant-picker-input][data-option-index="${btn.dataset.optionIndex}"][value="${CSS.escape(btn.dataset.optionValue || '')}"]`,
          );
          siblingInput?.classList.remove('disabled');
          return;
        }

        if (!this.variants?.length) return;

        const index = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.optionValue;

        const matchesPrefix = (variant) => {
          if (variant[`option${index + 1}`] !== value) return false;
          for (let i = 0; i < index; i += 1) {
            const selected = selectedOptions[i];
            if (selected && variant[`option${i + 1}`] !== selected) return false;
          }
          return true;
        };

        const hasAny = this.variants.some(matchesPrefix);
        const hasAvailable = this.variants.some((variant) => variant.available && matchesPrefix(variant));
        const isSoldOut = hasAny && !hasAvailable;
        const isNonExistent = !hasAny;

        // Sold-out stays selectable (visual only). Non-existent stays marked unavailable.
        btn.classList.toggle('is-unavailable', isSoldOut || isNonExistent);
        btn.classList.toggle('is-sold-out', isSoldOut);
        btn.classList.toggle('disabled', isSoldOut || isNonExistent);

        const input = this.querySelector(
          `[data-variant-picker-input][data-option-index="${index}"][value="${CSS.escape(value)}"]`,
        );
        if (input) {
          input.classList.toggle('disabled', isSoldOut || isNonExistent);
          // Never use native disabled — it blocks selecting sold-out values.
          input.removeAttribute('disabled');
          if (currentVariant?.[`option${index + 1}`] === value) {
            input.checked = true;
          }
        }
      });

      this.querySelectorAll('[data-variant-picker-select]').forEach((select) => {
        const index = parseInt(select.dataset.optionIndex, 10);
        Array.from(select.options).forEach((option) => {
          if (option.dataset.productUrl && this.isCombinedListingProductUrl(option.dataset.productUrl)) {
            option.disabled = false;
            return;
          }

          const value = option.value;
          const matchesPrefix = (variant) => {
            if (variant[`option${index + 1}`] !== value) return false;
            for (let i = 0; i < index; i += 1) {
              const selected = selectedOptions[i];
              if (selected && variant[`option${i + 1}`] !== selected) return false;
            }
            return true;
          };

          const hasAny = this.variants.some(matchesPrefix);
          const hasAvailable = this.variants.some((variant) => variant.available && matchesPrefix(variant));
          // Only block options that do not exist for the current prefix.
          option.disabled = !hasAny && !option.dataset.productUrl;
          option.classList.toggle('is-sold-out', hasAny && !hasAvailable);

          if (currentVariant?.[`option${index + 1}`] === value) {
            option.setAttribute('selected', '');
            select.value = value;
          } else {
            option.removeAttribute('selected');
          }
        });
      });
    }

    getCurrentVariantId() {
      return this.variantInput?.value || this.getVariantIdFromUrl();
    }

    getFeaturedMediaIdForVariant(variantId) {
      return (
        this.mediaMap?.variants?.find((entry) => String(entry.id) === String(variantId))
          ?.featuredMediaId || null
      );
    }

    getCurrentFeaturedMediaId() {
      return this.getFeaturedMediaIdForVariant(this.getCurrentVariantId());
    }

    readMediaMap() {
      const el = this.sectionEl?.querySelector('[data-product-media-map]');
      if (!el?.textContent) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (_e) {
        return null;
      }
    }
  }

  customElements.define('coco-variants', CocoVariants);
}

if (!window.__cocoCombinedListingSiblingBound) {
  window.__cocoCombinedListingSiblingBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a.product-sibling');
    if (!link?.href) return;
    if (link.closest('[is="quick-view"]')) return;

    const info = link.closest('product-info[data-update-url="false"]');
    if (!info) return;

    event.preventDefault();
    event.stopPropagation();
    if (link.classList.contains('is-current')) return;

    const variants = info.querySelector('coco-variants');
    variants?.renderProductInfo?.(null, info, { productUrl: link.href, swap: true });
  });
}

export { VARIANT_CHANGE };
